import { useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Check } from "lucide-react";
import Button from "@/components/site/Button";
import useSeo from "@/hooks/useSeo";
import { breadcrumbLd } from "@/lib/jsonld";
import { AANBOD_DETAILS } from "@/lib/aanbodDetails";
import { usePageContent, ContentLoading } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

// AANBOD_DETAILS blijft de bron voor boekingssleutel en CTA-type (code);
// alle teksten komen uit de database. DEFAULTS_BY_SLUG is de terugval.
const DEFAULTS_BY_SLUG = Object.fromEntries(
    Object.values(AANBOD_DETAILS).map((d) => {
        const p = `aanbod-detail.${d.slug}`;
        const map = {
            [`${p}.seo-title`]: `${d.naam} | Human Design Studio`,
            [`${p}.seo-description`]: d.intro,
            [`${p}.eyebrow`]: "Aanbod",
            [`${p}.naam`]: d.naam,
            [`${p}.duur`]: d.duur,
            [`${p}.prijs`]: d.prijs,
            [`${p}.intro`]: d.intro,
            [`${p}.label-voor-wie`]: "Voor wie",
            [`${p}.voor-wie`]: d.voorWie,
            [`${p}.label-wat-doen`]: "Wat we doen",
            [`${p}.wat-doen`]: d.watDoen,
            [`${p}.label-meeneemt`]: "Wat je meeneemt",
            [`${p}.meeneemt`]: d.meeneemt,
            [`${p}.label-praktisch`]: "Praktisch",
            [`${p}.praktisch`]: d.praktisch,
            [`${p}.terug-link`]: "Terug naar aanbod",
        };
        if (d.cta.type === "book") {
            map[`${p}.cta-heading`] = "Klaar om te boeken?";
            map[`${p}.cta-text`] =
                "Kies een moment dat bij je past. We starten direct met jouw sessie.";
            map[`${p}.cta-knop`] = "Boek een sessie";
        } else {
            map[`${p}.cta-heading`] = "Zet me op de wachtlijst";
            map[`${p}.cta-text`] =
                "Er zijn nog geen vaste data. Laat je gegevens achter en je hoort het als eerste zodra een avond gepland is.";
            map[`${p}.cta-knop`] = "Zet me op de wachtlijst";
            map[`${p}.cta-bevestiging`] =
                "Je staat op de wachtlijst. Zodra er een avond gepland is, laat ik het je als eerste weten.";
        }
        return [d.slug, map];
    })
);

export default function AanbodDetail() {
    const { slug } = useParams();
    const d = AANBOD_DETAILS[slug];
    const page = d ? `aanbod-detail.${d.slug}` : "aanbod-detail.__onbekend__";
    const c = usePageContent(page, d ? DEFAULTS_BY_SLUG[d.slug] : {});

    useSeo({
        title:
            c && d
                ? c.t(`${page}.seo-title`)
                : d
                  ? `${d.naam} | Human Design Studio`
                  : undefined,
        description:
            c && d ? c.t(`${page}.seo-description`) : d ? d.intro : undefined,
        path: d ? `/aanbod/${d.slug}` : undefined,
        jsonLd: d ? [breadcrumbLd(d.naam, `/aanbod/${d.slug}`)] : undefined,
    });

    if (!d) return <Navigate to="/aanbod" replace />;
    if (!c) return <ContentLoading />;

    const t = (suffix) => c.t(`${page}.${suffix}`);

    return (
        <div data-testid={`page-aanbod-${d.slug}`}>
            {/* HERO */}
            <section className="mx-auto max-w-[1100px] px-5 md:px-10 pt-24 md:pt-40 pb-10 md:pb-14">
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)]/90 mb-6"
                >
                    {t("eyebrow")}
                </motion.p>
                <div className="overflow-hidden">
                    <motion.h1
                        initial={{ y: "110%" }}
                        animate={{ y: "0%" }}
                        transition={{ duration: 1, ease: EASE }}
                        className="font-[Fraunces] text-[11vw] md:text-[7vw] lg:text-[72px] leading-[1.02] tracking-[-0.02em] text-[var(--hds-ink)]"
                        style={{ fontWeight: 300 }}
                    >
                        {t("naam")}
                    </motion.h1>
                </div>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
                    className="mt-6 flex items-center gap-3 text-[12.5px] tracking-[0.18em] uppercase text-[var(--hds-ink)]/55"
                    data-testid="aanbod-detail-meta"
                >
                    <span>{t("duur")}</span>
                    <span className="sr-only">, </span>
                    <span
                        aria-hidden
                        className="inline-block w-1 h-1 rounded-full bg-[var(--hds-clay)]/60"
                    />
                    <span
                        className="font-[Fraunces] text-[18px] tracking-normal normal-case text-[var(--hds-clay)]"
                        style={{ fontWeight: 400, fontStyle: "italic" }}
                    >
                        {t("prijs")}
                    </span>
                </motion.div>
            </section>

            {/* INTRO + SECTIES */}
            <section className="mx-auto max-w-[1100px] px-5 md:px-10 pb-16 md:pb-24">
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="font-[Fraunces] text-[22px] md:text-[26px] leading-[1.3] tracking-[-0.01em] text-[var(--hds-ink)] max-w-[62ch]"
                    style={{ fontWeight: 400 }}
                >
                    {t("intro")}
                </motion.p>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
                    <Blok eyebrow={t("label-voor-wie")} body={t("voor-wie")} />
                    <Blok eyebrow={t("label-wat-doen")} body={t("wat-doen")} />
                    <Blok
                        eyebrow={t("label-meeneemt")}
                        list={c.list(`${page}.meeneemt`)}
                        testId={`detail-meeneemt-${d.slug}`}
                    />
                    <Blok eyebrow={t("label-praktisch")} body={t("praktisch")} />
                </div>
            </section>

            {/* CTA */}
            <CTABlok
                detail={d}
                texts={{
                    heading: t("cta-heading"),
                    text: t("cta-text"),
                    knop: t("cta-knop"),
                    bevestiging: t("cta-bevestiging"),
                    terugLink: t("terug-link"),
                }}
            />
        </div>
    );
}

function Blok({ eyebrow, body, list, testId }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="md:col-span-6"
        >
            <p className="text-[11.5px] tracking-[0.24em] uppercase text-[var(--hds-clay)] mb-3">
                {eyebrow}
            </p>
            {body && (
                <p className="text-[16px] leading-[1.65] text-[var(--hds-ink)]/82 max-w-[54ch]">
                    {body}
                </p>
            )}
            {list && (
                <ul className="space-y-2.5" data-testid={testId}>
                    {list.map((item, i) => (
                        <li
                            key={i}
                            className="flex gap-3 text-[15.5px] leading-[1.6] text-[var(--hds-ink)]/82"
                        >
                            <span
                                aria-hidden
                                className="mt-2.5 shrink-0 w-1.5 h-1.5 rounded-full bg-[var(--hds-clay)]"
                            />
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
            )}
        </motion.div>
    );
}

function CTABlok({ detail, texts }) {
    return (
        <section className="mx-auto max-w-[1100px] px-5 md:px-10 pb-24 md:pb-36">
            <div
                className="relative overflow-hidden rounded-[18px] bg-[var(--hds-clay)] text-[var(--hds-card)] px-7 md:px-12 py-12 md:py-16"
                data-testid={`detail-cta-${detail.slug}`}
            >
                <div
                    aria-hidden
                    className="pointer-events-none absolute -right-20 -top-16 w-[260px] h-[260px] rounded-full"
                    style={{
                        background:
                            "radial-gradient(circle at 35% 30%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%)",
                    }}
                />
                {detail.cta.type === "book" ? (
                    <BookCTA detail={detail} texts={texts} />
                ) : (
                    <WaitlistCTA texts={texts} />
                )}
                <div className="relative mt-10">
                    <Link
                        to="/aanbod"
                        className="text-[13.5px] text-[var(--hds-card)]/70 hover:text-[var(--hds-card)] underline underline-offset-2"
                        data-testid="detail-back-aanbod"
                    >
                        {texts.terugLink}
                    </Link>
                </div>
            </div>
        </section>
    );
}

function BookCTA({ detail, texts }) {
    return (
        <div className="relative max-w-[46ch]">
            <h2
                className="font-[Fraunces] text-[30px] md:text-[42px] leading-[1.08] tracking-[-0.02em]"
                style={{ fontWeight: 300 }}
            >
                {texts.heading}
            </h2>
            <p className="mt-5 text-[16px] leading-[1.6] text-[var(--hds-card)]/85">
                {texts.text}
            </p>
            <div className="mt-8">
                <Button
                    variant="onClayPrimary"
                    icon="arrow"
                    to={`/boeken?sessie=${detail.slug}`}
                    data-testid={`detail-boek-${detail.slug}`}
                >
                    {texts.knop}
                </Button>
            </div>
        </div>
    );
}

function WaitlistCTA({ texts }) {
    const mountRef = useRef(Date.now());
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ naam: "", email: "", privacy_akkoord: false, website: "" });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [serverError, setServerError] = useState("");

    const submit = async () => {
        const e = {};
        if (!form.naam.trim()) e.naam = "Vul je naam in.";
        if (!emailOk(form.email)) e.email = "Vul een geldig e-mailadres in.";
        if (!form.privacy_akkoord)
            e.privacy_akkoord = "Je moet akkoord gaan om je aan te melden.";
        setErrors(e);
        if (Object.keys(e).length) return;
        setSubmitting(true);
        setServerError("");
        try {
            await axios.post(`${API}/workshop-waitlist`, {
                ...form,
                elapsed_ms: Date.now() - mountRef.current,
            });
            setDone(true);
        } catch (err) {
            const d = err.response?.data?.detail;
            setServerError(typeof d === "string" ? d : "Er ging iets mis. Probeer het opnieuw.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="relative max-w-[46ch]">
            <h2
                className="font-[Fraunces] text-[30px] md:text-[42px] leading-[1.08] tracking-[-0.02em]"
                style={{ fontWeight: 300 }}
            >
                {texts.heading}
            </h2>
            <p className="mt-5 text-[16px] leading-[1.6] text-[var(--hds-card)]/85">
                {texts.text}
            </p>
            {done ? (
                <div
                    data-testid="detail-waitlist-confirmation"
                    className="mt-8 rounded-[12px] bg-[var(--hds-card)]/15 border border-[var(--hds-card)]/25 p-4 flex gap-3 text-[14.5px] leading-[1.55] text-[var(--hds-card)]"
                >
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-[var(--hds-card)] text-[var(--hds-clay)] flex items-center justify-center shrink-0">
                        <Check size={14} strokeWidth={2} />
                    </span>
                    <span>{texts.bevestiging}</span>
                </div>
            ) : open ? (
                <div className="mt-6 space-y-3">
                    <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                        style={{ position: "absolute", left: "-9999px", opacity: 0 }}
                        aria-hidden
                    />
                    <input
                        type="text"
                        placeholder="Naam"
                        value={form.naam}
                        onChange={(e) => setForm({ ...form, naam: e.target.value })}
                        data-testid="detail-waitlist-naam"
                        className={`w-full rounded-[10px] bg-[var(--hds-card)] text-[var(--hds-ink)] border outline-none px-4 py-3 text-[14.5px] ${errors.naam ? "border-red-400" : "border-transparent focus:border-[var(--hds-sand)]"}`}
                    />
                    {errors.naam && <p className="text-[12.5px] text-red-200">{errors.naam}</p>}
                    <input
                        type="email"
                        placeholder="E-mailadres"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="detail-waitlist-email"
                        className={`w-full rounded-[10px] bg-[var(--hds-card)] text-[var(--hds-ink)] border outline-none px-4 py-3 text-[14.5px] ${errors.email ? "border-red-400" : "border-transparent focus:border-[var(--hds-sand)]"}`}
                    />
                    {errors.email && <p className="text-[12.5px] text-red-200">{errors.email}</p>}
                    <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.privacy_akkoord}
                            onChange={(e) => setForm({ ...form, privacy_akkoord: e.target.checked })}
                            data-testid="detail-waitlist-privacy"
                            className="mt-1 w-4 h-4 shrink-0"
                        />
                        <span className="text-[13px] leading-[1.5] text-[var(--hds-card)]/85">
                            Ik ga akkoord met de{" "}
                            <Link to="/privacy" className="underline underline-offset-2">privacyverklaring</Link>.
                        </span>
                    </label>
                    {errors.privacy_akkoord && (
                        <p className="text-[12.5px] text-red-200">{errors.privacy_akkoord}</p>
                    )}
                    {serverError && (
                        <p className="text-[13px] text-red-200">{serverError}</p>
                    )}
                    <div className="pt-1 flex flex-wrap gap-3 items-center">
                        <Button
                            variant="onClayPrimary"
                            icon="arrow"
                            onClick={submit}
                            disabled={submitting}
                            data-testid="detail-waitlist-submit"
                        >
                            {submitting ? "Bezig..." : "Aanmelden"}
                        </Button>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="text-[13px] text-[var(--hds-card)]/80 hover:text-[var(--hds-card)] underline underline-offset-2"
                        >
                            Annuleren
                        </button>
                    </div>
                </div>
            ) : (
                <div className="mt-8">
                    <Button
                        variant="onClayPrimary"
                        icon="arrow"
                        onClick={() => setOpen(true)}
                        data-testid="detail-waitlist-open"
                    >
                        {texts.knop}
                    </Button>
                </div>
            )}
        </div>
    );
}
